import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GeneralV3Response, LoadingType } from "../../../types/CommonTypes";
import { fetchPracticeProvidersById, savePracticeProvider, inactivePracticeProviders, fetchActivePracticeProvidersById, fetchActivePracticeProvidersByIds, fetchPracticeProviderByUserId, fetchAllUsedProviderIds, fetchAllProviderUsedPracticeIds, fetchIsLastProviderByUserId, providerSearchService, fetchAllProviders, fetchProviderTemplatesByProviderId } from "../../../service/PracticeService";
import { useAppSelector } from "./../redux-hooks";

export type PracticeProviderInfo = {
    providerId: number;
    practiceId: string;
    title: string;
    firstName: string;
    middleName: string;
    lastName: string;
    npi: string;
    ptan: string;
    taxId: string;
    createdDate: Date;
    inactiveDate: Date;
    billingType?: string;
    locationId?: number; 
    email: string;
    otherTitle?: string;
  }

export type FetchActiveProvidersParams = {
  productId: string;
  onlyActiveRecords: boolean;
}

export type FetchActiveProvidersListParams = {
  practiceIds: number[];
  onlyActiveRecords: boolean;
}

export type SearchProviderRequest = {
  practiceIds: number[],
  firstName: string,
  lastName: string,
  email: string,
  hasTemplates: boolean | null,
  currentPage: number;
  itemsPerPage: number;
  downloadState?: boolean; // CS-1685
  sortBy?: "practiceName" | "providerName" | "email";
  sortOrder?: "asc" | "desc";
};

export interface PaginationProviderList {
  content: any[];
  totalElements: number;
  totalPages: number;
  size: number;
}

export type providerTemplateInfo = {
  templateId: number;
  providerId: number;
  procedureName: string;
  inactiveDate: string;
  templateJson: any;
}

type PracticeProviderState = {
    practiceProvidersInfo?: PracticeProviderInfo[] | null;
    practiceProvidersDataFetchStatus?: LoadingType
    practiceProvidersSaveStatus?: LoadingType
    practiceProvidersInactiveStatus?: LoadingType
    fetchProviderListStatus: LoadingType;
    practiceProviderInfo?: PracticeProviderInfo | null;
    usedPracticeProviderIds: number[];
    allProviderUsedPracticeIds: number[];
    isLastPracticeProvider: boolean;
    searchProviderList: PaginationProviderList | null;
    getProviderListStatus?: LoadingType;
    getProviderList: PracticeProviderInfo[] | null;
    getProviderTemplatesStatus?: LoadingType;
    providerTemplates: providerTemplateInfo[] | null;
  };

  const initialState: PracticeProviderState = {
    practiceProvidersInfo: undefined,
    practiceProvidersDataFetchStatus: "idle",
    practiceProvidersSaveStatus: "idle",
    practiceProvidersInactiveStatus: "idle",
    fetchProviderListStatus: 'idle',
    practiceProviderInfo: undefined,
    usedPracticeProviderIds: [],
    allProviderUsedPracticeIds: [],
    isLastPracticeProvider: false,
    searchProviderList: null,
    getProviderListStatus: "idle",
    getProviderList: [],
    getProviderTemplatesStatus: "idle",
    providerTemplates: []
  };

  export const fetchPracticeProvidersData = createAsyncThunk("fetchPracticeProvidersData", async (practiceId: string) => {
    const response = await fetchPracticeProvidersById(practiceId);
    // return response.data;
    return response?.data; // CS-1984
  });

  export const fetchPracticeProviderByUser = createAsyncThunk("fetchPracticeProviderByUser", async (userId: string) => {
    const response = await fetchPracticeProviderByUserId(userId);
    return response.data;
  });

  export const fetchIsLastProviderByUser = createAsyncThunk("fetchIsLastProviderByUser", async (userId: string) => {
    const response = await fetchIsLastProviderByUserId(userId);
    return response.data;
  });

  export const fetchUsedPracticeProviderIDs = createAsyncThunk("fetchUsedPracticeProviderIDs", async () => {
    const response = await fetchAllUsedProviderIds();
    return response.data;
  });
  
  export const fetchAllProviderUsedPracticeIDs = createAsyncThunk("fetchAllProviderUsedPracticeIDs", async () => {
    const response = await fetchAllProviderUsedPracticeIds();
    return response.data;
  });

  export const fetchActivePracticeProvidersData = createAsyncThunk("fetchActivePracticeProvidersData", async ({ productId, onlyActiveRecords }: FetchActiveProvidersParams) => {
    const response = await fetchActivePracticeProvidersById(productId, onlyActiveRecords);
    return response.data;
  });

  export const fetchActivePracticeProviders = createAsyncThunk<any, { data: FetchActiveProvidersListParams, skipLoader?:boolean}>("fetchActivePracticeProviders", async ({ data, skipLoader = false }) => {
    const { practiceIds, onlyActiveRecords } = data;
    const response = await fetchActivePracticeProvidersByIds(practiceIds, onlyActiveRecords, { 'x-skip-loader': skipLoader.toString() });
    return response.data;
  });

  export const savePracticeProviders = createAsyncThunk("savePracticeProvider", async (data: PracticeProviderInfo, { rejectWithValue }) => {
    try{
    const response = await savePracticeProvider(data);
    return response.data;
    }catch(error){
      //@ts-ignore
      return rejectWithValue(error?.response?.data);
    }
  });

  export const inactivePracticeProvider = createAsyncThunk("inactivePracticeProvider", async (data: any, { rejectWithValue }) => {
    try{
    const response = await inactivePracticeProviders(data.providerId, data.inactiveDate);
    return response.data;
    }catch(error){
      //@ts-ignore
      return rejectWithValue(error?.response?.data);
    }
  });

  export const searchProviders = createAsyncThunk("searchProviders", async (providerSearchRequest: SearchProviderRequest) => {
    const response = await providerSearchService(providerSearchRequest);
    const v3Response = response.data;
    return v3Response.data;
  });

  export const getAllProviders = createAsyncThunk("getAllProviders", async () => {
    const response = await fetchAllProviders();
    // return response.data;
    return response?.data; // CS-1984
  });

  export const fetchProviderTemplatesByProvider = createAsyncThunk("fetchProviderTemplatesByProvider", async (providerId: string) => {
    const response = await fetchProviderTemplatesByProviderId(providerId);
    // return response.data;
    return response?.data; // CS-1984
  });

  const practiceProvidersSlice = createSlice({
    name: "practiceProviders",
    initialState,
    reducers: {
      updatePracticeProvidersInfoObject: (state: PracticeProviderState, action) => {
        state.practiceProvidersInfo = action.payload;
      },
      resetPracticeProvidersInfo : (state) =>{
        state.practiceProvidersInfo = null;
      },
      resetSearchedProviderList : (state) => {
        state.searchProviderList = null;
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchPracticeProvidersData.pending, (state) => {
          state.practiceProvidersDataFetchStatus = "loading";
        })
        .addCase(fetchPracticeProvidersData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersDataFetchStatus = "success";
          // state.practiceProvidersInfo = action.payload.data;
          state.practiceProvidersInfo = action.payload?.data; // CS-1984
        })
        .addCase(fetchPracticeProvidersData.rejected, (state, action) => {
          state.practiceProvidersDataFetchStatus = 'error';
        })
        .addCase(fetchPracticeProviderByUser.pending, (state) => {
          state.practiceProvidersDataFetchStatus = "loading";
        })
        .addCase(fetchPracticeProviderByUser.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersDataFetchStatus = "success";
          state.practiceProviderInfo = action.payload.data;
        })
        .addCase(fetchPracticeProviderByUser.rejected, (state, action) => {
          state.practiceProvidersDataFetchStatus = 'error';
        })
        .addCase(fetchUsedPracticeProviderIDs.pending, (state) => {
          state.practiceProvidersDataFetchStatus = "loading";
        })
        .addCase(fetchUsedPracticeProviderIDs.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersDataFetchStatus = "success";
          // state.usedPracticeProviderIds = action.payload.data; // CS-1984: guarded below
          state.usedPracticeProviderIds = action.payload?.data || []; // CS-1984
        })
        .addCase(fetchUsedPracticeProviderIDs.rejected, (state, action) => {
          state.practiceProvidersDataFetchStatus = 'error';
        })
        .addCase(fetchAllProviderUsedPracticeIDs.pending, (state) => {
          state.practiceProvidersDataFetchStatus = "loading";
        })
        .addCase(fetchAllProviderUsedPracticeIDs.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersDataFetchStatus = "success";
          // state.allProviderUsedPracticeIds = action.payload.data; // CS-1984: guarded below
          state.allProviderUsedPracticeIds = action.payload?.data || []; // CS-1984
        })
        .addCase(fetchAllProviderUsedPracticeIDs.rejected, (state, action) => {
          state.practiceProvidersDataFetchStatus = 'error';
        })
        .addCase(fetchActivePracticeProvidersData.pending, (state) => {
          state.practiceProvidersDataFetchStatus = "loading";
        })
        .addCase(fetchActivePracticeProvidersData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersDataFetchStatus = "success";
          state.practiceProvidersInfo = action.payload.data;
        })
        .addCase(fetchActivePracticeProvidersData.rejected, (state, action) => {
          state.practiceProvidersDataFetchStatus = 'error';
        })
        .addCase(savePracticeProviders.pending, (state) => {
          state.practiceProvidersSaveStatus = "loading";
        })
        .addCase(savePracticeProviders.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersSaveStatus = "success";
          // state.practiceLocationsInfo = action.payload.data;
        })
        .addCase(savePracticeProviders.rejected, (state, action) => {
          state.practiceProvidersSaveStatus = 'error';
        })
        .addCase(inactivePracticeProvider.pending, (state) => {
          state.practiceProvidersInactiveStatus = "loading";
        })
        .addCase(inactivePracticeProvider.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersInactiveStatus = "success";
          // state.practiceLocationsInfo = action.payload.data;
        })
        .addCase(inactivePracticeProvider.rejected, (state, action) => {
          state.practiceProvidersInactiveStatus = 'error';
        })
        .addCase(fetchActivePracticeProviders.pending, (state) => {
          state.practiceProvidersDataFetchStatus = "loading";
        })
        .addCase(fetchActivePracticeProviders.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersDataFetchStatus = "success";
          state.practiceProvidersInfo = action.payload.data;
        })
        .addCase(fetchActivePracticeProviders.rejected, (state, action) => {
          state.practiceProvidersDataFetchStatus = 'error';
        })
        .addCase(fetchIsLastProviderByUser.pending, (state) => {
          state.practiceProvidersDataFetchStatus = "loading";
        })
        .addCase(fetchIsLastProviderByUser.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProvidersDataFetchStatus = "success";
          state.isLastPracticeProvider = action.payload.data;
        })
        .addCase(fetchIsLastProviderByUser.rejected, (state, action) => {
          state.practiceProvidersDataFetchStatus = 'error';
        })
        .addCase(searchProviders.pending, (state) => {
          state.fetchProviderListStatus = "loading";
        })
        .addCase(searchProviders.fulfilled, (state, action) => {
          state.fetchProviderListStatus = "success";
          // state.searchProviderList = action.payload.searchPracticeProviderData; // CS-1685
          state.searchProviderList = action.payload?.searchPracticeProviderData; // CS-1984 // CS-1685
        })
        .addCase(searchProviders.rejected, (state, action) => {
          state.fetchProviderListStatus = 'error';
        })
        .addCase(getAllProviders.pending, (state) => {
          state.getProviderListStatus = "loading";
        })
        .addCase(getAllProviders.fulfilled, (state, action) => {
          state.getProviderListStatus = "success";
          // state.getProviderList = action.payload.data;
          state.getProviderList = action.payload?.data; // CS-1984
        })
        .addCase(getAllProviders.rejected, (state, action) => {
          state.getProviderListStatus = 'error';
        })
        .addCase(fetchProviderTemplatesByProvider.pending, (state) => {
          state.getProviderTemplatesStatus = "loading";
        })
        .addCase(fetchProviderTemplatesByProvider.fulfilled, (state, action) => {
          state.getProviderTemplatesStatus = "success";
          // state.providerTemplates = action.payload.data;
          state.providerTemplates = action.payload?.data; // CS-1984
        })
        .addCase(fetchProviderTemplatesByProvider.rejected, (state, action) => {
          state.getProviderTemplatesStatus = 'error';
        });
    },
  });

export const { updatePracticeProvidersInfoObject, resetPracticeProvidersInfo, resetSearchedProviderList } = practiceProvidersSlice.actions;
export const usePracticeProvidersSlice = () => useAppSelector((state) => state.practiceProvidersSlice);
export default practiceProvidersSlice.reducer;